import { ArrowBackIos } from "@mui/icons-material";
import { Box, Button, Checkbox, FormControlLabel, Grid, Radio, RadioGroup, Stack, Tooltip, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { Controller, FormProvider, useForm } from "react-hook-form";

import PhoneNumberInput from "src/components/shared/PhoneNumberInput";
import { transferAddressType } from "src/constant";

import useCustomAddressDetail from "src/hooks/user/customer/useCustomAddressDetail";
import useCustomerAddressCreate from "src/hooks/user/customer/useCustomerAddressCreate";
import useCustomerAddressUpdate from "src/hooks/user/customer/useCustomerAddressUpdate";

import CustomerAutocomplete from "src/pages/user/customer/CustomerAutocomplete";
import CustomerInput from "src/pages/user/customer/CustomerInput";
import FieldLabel from "src/pages/user/customer/FieldLabel";

import { checkVietnam, mapToOptions } from "src/pages/user/customer/utils/mapping";

import { useAddressData } from "src/pages/user/customer/utils/useAddressData";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { useEffect, useState } from "react";

import SearchList from "src/components/shared/SearchList";

const INITIAL_ADDRESS = {
  recipientName: "",
  phone: "",
  detailAddress: "",
  province: null,
  district: null,
  ward: null,
  isDefault: false,
  country: null,
  addressLevel: 2,
};

const schema = yup.object().shape({
  recipientName: yup.string().required("Tên người nhận là bắt buộc"),

  phone: yup
    .string()
    .required("Số điện thoại là bắt buộc")
    .matches(/^\+?[0-9]{5,15}$/, "Số điện thoại không hợp lệ"),

  detailAddress: yup.string().required("Địa chỉ cụ thể là bắt buộc"),

  province: yup
    .object()
    .required("Thành phố/ Tỉnh là bắt buộc")
    .when("country", {
      is: (country) => checkVietnam(country),
      then: (schema) => schema.required("Thành phố/ Tỉnh là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),

  district: yup
    .object()
    .required("Quận/ Huyện là bắt buộc")
    .when(["country", "addressLevel"], {
      is: (country, addressLevel) => checkVietnam(country) && addressLevel === 3,
      then: (schema) => schema.required("Quận/ Huyện là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),

  ward: yup
    .object()
    .required("Phường/ Xã là bắt buộc")
    .when("country", {
      is: (country) => checkVietnam(country),
      then: (schema) => schema.required("Phường/ Xã là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),

  country: yup.object().required("Quốc gia là bắt buộc"),

  addressLevel: yup.number().required("Cấp địa chỉ là bắt buộc"),
});

export default function ChangeAddressDialog({ customer, handleChangeAddress }) {
  const { trigger: updateAddress } = useCustomerAddressUpdate(customer?._id);
  const { trigger: addAddress } = useCustomerAddressCreate(customer?._id);
  const { data: customerAddress, mutate } = useCustomAddressDetail(customer?._id);

  const [activePhoneCode, setActivePhoneCode] = useState(null);

  const form = useForm({
    defaultValues: INITIAL_ADDRESS,
    resolver: yupResolver(schema),
  });

  const { control, watch, handleSubmit, setValue, reset } = form;

  const province = watch("province");
  const district = watch("district");
  const country = watch("country");
  const addressLevel = watch("addressLevel");

  const { countries, provinces, districts, wards } = useAddressData(province, district, addressLevel);

  const isVietnam = checkVietnam(country);

  useEffect(() => {
    if (countries?.data && !country) {
      const vietnamCountry = countries.data.find(checkVietnam);
      if (vietnamCountry) {
        setValue("country", vietnamCountry);
      }
    }
  }, [countries?.data, country, setValue]);

  const handleChangeAddressLevel = (level) => {
    setValue("district", null);
    setValue("ward", null);
    setValue("province", null);
    setValue("addressLevel", level);
  };

  const handleUpdateAddress = async (item) => {
    const res = await updateAddress({
      pathIncluded: {
        customerId: item?.customer,
        addressId: item?._id,
      },

      body: {
        recipientName: customer?.fullName,
        detailAddress: item?.detailAddress,
        phone: customer?.phone,
        isDefault: true,
        ward: item?.ward?._id,
        province: item?.province?.id,
        ward: item?.ward?.id,
      },
    });

    if (res?.success) {
      enqueueSnackbar("Thành công", {
        variant: "success",
      });

      mutate();
    } else {
      enqueueSnackbar(res?.error?.message ?? "Lỗi khi cập nhật địa chỉ", {
        variant: "error",
      });
    }
  };

  const handleAddAddress = async (values) => {
    const res = await addAddress({
      body: {
        ...values,
        recipientName: values.recipientName,
        detailAddress: values.detailAddress,
        phone: values.phone,
        isDefault: values.isDefault,
        ward: values.ward?._id ?? values.ward?.id ?? undefined,
        country: values.country?.id ?? undefined,
        province: values.province?.id ?? undefined,
        district: values.district?.id ?? undefined,
      },
    });

    if (res?.success) {
      mutate();

      enqueueSnackbar("Thêm địa chỉ thành công!", {
        variant: "success",
      });
      reset(INITIAL_ADDRESS);
    } else {
      enqueueSnackbar(res?.error?.message ?? "Lỗi khi thêm địa chỉ", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (country) {
      const nextPhoneCode = countries?.data?.find((v) => v._id === country?.id)?.phoneCode ?? null;

      setActivePhoneCode((prev) => (prev === nextPhoneCode ? prev : nextPhoneCode));
    } else {
      setActivePhoneCode(null);
    }
  }, [country, countries?.data]);

  return (
    <FormProvider {...form}>
      <Grid
        container
        spacing={1}
      >
        {/* HEADER */}
        <Grid
          xs={12}
          item
          sx={{
            borderBottom: "1px solid var(--light-grey)",
            mb: "12px",
          }}
        >
          <Button
            onClick={handleChangeAddress}
            startIcon={<ArrowBackIos />}
          >
            Tiếp tục tạo đơn hàng
          </Button>
        </Grid>

        {/* FORM */}
        <Grid
          item
          xs={6}
        >
          <Grid
            container
            xs={12}
            spacing={2}
          >
            {/* DEFAULT ADDRESS */}
            <Grid
              item
              p={0}
            >
              <Controller
                name="isDefault"
                control={control}
                render={({ field }) => (
                  <Stack
                    direction="row"
                    alignItems="center"
                    ml={-1.5}
                  >
                    <Checkbox
                      checked={field.value}
                      onChange={() => field.onChange(!field.value)}
                    />

                    <Typography mb={0.5}>Đặt làm địa chỉ mặc định</Typography>
                  </Stack>
                )}
              />
            </Grid>

            {/* RECIPIENT NAME */}
            <CustomerInput
              name="recipientName"
              label="Tên người nhận"
              required
            />

            {/* COUNTRY */}
            <Grid
              item
              xs={12}
            >
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <SearchList
                    {...field}
                    params={{
                      path: "/api/v1/countries",
                    }}
                    label="Quốc gia"
                    fullWidth
                    value={field.value ?? country ?? ""}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>

            {/* PHONE */}
            <Grid
              item
              xs={12}
            >
              <PhoneNumberInput
                name="phone"
                label="Số điện thoại"
                width="100%"
                control={control}
                phoneCode={activePhoneCode}
                required
              />
            </Grid>

            {/* DETAIL ADDRESS */}
            <CustomerInput
              name="detailAddress"
              label="Địa chỉ cụ thể"
              placeholder="Địa chỉ cụ thể"
              required
            />

            {/* VIETNAM ADDRESS */}
            {isVietnam && (
              <>
                {/* ADDRESS LEVEL */}
                <Grid
                  item
                  xs={12}
                >
                  <RadioGroup
                    row
                    value={addressLevel}
                    onChange={(e) => handleChangeAddressLevel(Number(e.target.value))}
                  >
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label={<FieldLabel label="Địa chỉ 2 cấp" />}
                    />
                    <FormControlLabel
                      value={3}
                      control={<Radio />}
                      label={<FieldLabel label="Địa chỉ 3 cấp" />}
                    />
                  </RadioGroup>
                </Grid>

                {/* PROVINCE */}
                <CustomerAutocomplete
                  name="province"
                  label="Thành phố/ Tỉnh"
                  options={mapToOptions(provinces)}
                  xs={12 / addressLevel}
                  required
                />

                {/* DISTRICT */}
                {addressLevel === 3 && (
                  <CustomerAutocomplete
                    name="district"
                    label="Quận/ Huyện"
                    options={mapToOptions(districts)}
                    xs={12 / 3}
                    disabled={!province}
                    required
                  />
                )}

                {/* WARD */}
                <CustomerAutocomplete
                  name="ward"
                  label="Phường/ Xã"
                  options={mapToOptions(wards)}
                  xs={12 / addressLevel}
                  required
                  disabled={addressLevel === 2 ? !province : !district}
                />
              </>
            )}

            {/* SUBMIT */}
            <Grid
              item
              xs={12}
              justifyContent="flex-end"
            >
              <Button
                variant="contained"
                type="submit"
                fullWidth
                onClick={handleSubmit(handleAddAddress)}
              >
                Thêm địa chỉ
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {/* ADDRESS LIST */}
        <Grid
          item
          xs={6}
        >
          <Typography
            fontWeight="bold"
            mb={2}
          >
            Danh sách địa chỉ:
          </Typography>

          {Array.isArray(customerAddress?.data) && customerAddress.data.length ? (
            customerAddress.data.map((v) => (
              <Tooltip
                key={v._id}
                title="Chọn địa chỉ này làm mặc định"
              >
                <Box
                  sx={{
                    background: v.isDefault ? "var(--success)" : "var(--light-grey)",
                    mb: "12px",
                    borderRadius: "6px",
                    p: "6px 12px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    {v.isDefault && <Typography fontWeight="bold">Mặc định</Typography>}
                    <Typography fontWeight="bold">{v.recipientName ?? "Không có tên"}</Typography>
                    <Typography>{transferAddressType[v.type]}</Typography>
                    <Typography>{v.phone}</Typography>
                    <Typography>{v.fullAddress}</Typography>
                  </Box>

                  <Stack
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Checkbox
                      checked={v.isDefault}
                      onClick={() => handleUpdateAddress(v)}
                    />
                  </Stack>
                </Box>
              </Tooltip>
            ))
          ) : (
            <Typography variant="body2">Chưa có địa chỉ</Typography>
          )}
        </Grid>
      </Grid>
    </FormProvider>
  );
}
